import React, { useState } from 'react';
import { ShoppingCart, CheckCircle, XCircle, RefreshCw, ExternalLink, Clock, AlertTriangle } from 'lucide-react';
import Button from '../ui/Button';

interface WooCommerceStatus {
  enabled: boolean;
  baseUrl: string;
  lastSyncStatus: 'idle' | 'running' | 'success' | 'error';
  lastSuccessfulSyncAt: string | null;
  lastSyncError: string;
  syncMode: 'manual' | 'scheduled';
  entities: string[];
}

interface ActiveIntegrationCardProps {
  status: WooCommerceStatus;
  onSync: () => void;
  isSyncing: boolean;
}

const ActiveIntegrationCard: React.FC<ActiveIntegrationCardProps> = ({
  status,
  onSync,
  isSyncing,
}) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusBadge = () => {
    switch (status.lastSyncStatus) {
      case 'running':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
            <RefreshCw className="w-3 h-3 animate-spin" />
            Syncing...
          </span>
        );
      case 'success':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
            <CheckCircle className="w-3 h-3" />
            Synced
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
            <XCircle className="w-3 h-3" />
            Error
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
            <Clock className="w-3 h-3" />
            Idle
          </span>
        );
    }
  };

  const maskUrl = (url: string) => {
    try {
      const parsed = new URL(url);
      return parsed.hostname;
    } catch {
      return url;
    }
  };

  return (
    <div className="p-6 rounded-lg border dark:border-gray-700 border-gray-200 dark:bg-surface bg-white">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/30">
            <ShoppingCart className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold dark:text-white text-gray-900">WooCommerce</h3>
              {status.enabled ? (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Active
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                  Disabled
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 mt-1 text-sm text-gray-600 dark:text-gray-400">
              <ExternalLink className="w-3 h-3" />
              {maskUrl(status.baseUrl)}
            </div>
          </div>
        </div>
        {getStatusBadge()}
      </div>

      {/* Sync details */}
      <div className="mt-4 pt-4 border-t dark:border-gray-700 border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-500">Last Sync</span>
            <p className="font-medium dark:text-white text-gray-900 mt-0.5">
              {formatDate(status.lastSuccessfulSyncAt)}
            </p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-500">Sync Mode</span>
            <p className="font-medium dark:text-white text-gray-900 mt-0.5 capitalize">
              {status.syncMode}
            </p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-500">Synced Entities</span>
            <p className="font-medium dark:text-white text-gray-900 mt-0.5 capitalize">
              {status.entities.join(', ')}
            </p>
          </div>
        </div>

        {/* Error message */}
        {status.lastSyncStatus === 'error' && status.lastSyncError && (
          <div className="mt-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">{status.lastSyncError}</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex justify-end">
          <Button
            onClick={onSync}
            disabled={!status.enabled || isSyncing || status.lastSyncStatus === 'running'}
            isLoading={isSyncing || status.lastSyncStatus === 'running'}
            variant="secondary"
            size="sm"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Sync Now
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ActiveIntegrationCard;
